import React, { useState, useEffect } from "react";
import {
  Table,
  Typography,
  Button,
  Card,
  Space,
  Input,
  Modal,
  Form,
  message,
  Tabs,
  Tag,
  Dropdown,
  Progress,
  Switch,
  Divider,
  Alert,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import {
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  UnlockOutlined,
  MoreOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import { supabase } from "../lib/supabase";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import { resetUserPassword } from "../services/adminUsers";
import { AdminUserEmailPreferences } from "../components/AdminUserEmailPreferences";
import { AgentUsersManager } from "../components/admin/AgentUsersManager";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const Container = styled.div`
  padding: 24px;
`;

const ActionButton = styled(Button)`
  margin-right: 8px;
`;

// Add compact styles for the table
const CompactTable = styled.div`
  .compact-table {
    .ant-table {
      font-size: 13px;
    }

    .ant-table-thead > tr > th {
      padding: 8px 16px;
    }

    .ant-table-tbody > tr > td {
      padding: 4px 16px;
    }

    .ant-table-cell {
      white-space: nowrap;
    }

    .ant-tag {
      margin-right: 0;
      font-size: 11px;
      padding: 0 4px;
      line-height: 18px;
    }

    .ant-btn {
      font-size: 12px;
      padding: 0 8px;
      height: 24px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

// Define interface for our user type without extending the Supabase User type
interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  user_metadata: {
    role?: string;
    first_name?: string;
    last_name?: string;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
  app_metadata: {
    provider?: string;
    providers?: string[];
  };
  is_admin?: boolean;
  email_confirmed_at: string | null;
  banned: boolean;
  company_names: string[]; // Changed from optional to required with a default empty array
  companies?: Array<{
    id: string;
    company_id: string;
    user_id: string;
    created_at: string;
    company: {
      id: string;
      name: string;
      billing_email?: string;
      tax_id?: string;
      address?: string;
      city?: string;
      country?: string;
      active?: boolean;
      company_type?: string;
      created_at: string;
    };
  }>;
}

interface AdminUser {
  user_id: string;
  created_at: string;
  name?: string | null;
  email?: string;
}

// Add pagination and server response interface
interface PaginationParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: "ascend" | "descend";
}

interface UsersResponse {
  users: AuthUser[];
  total: number;
  page: number;
  pageSize: number;
}

const UsersAdmin: React.FC = () => {
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState("1");

  // Add pagination and sorting state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<
    "ascend" | "descend" | undefined
  >();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Add state for password reset modal
  const [passwordResetModalVisible, setPasswordResetModalVisible] =
    useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);

  // Add state for email change modal
  const [emailChangeModalVisible, setEmailChangeModalVisible] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeForm] = Form.useForm();

  // Add state for admin confirmation modals
  const [firstConfirmModalVisible, setFirstConfirmModalVisible] =
    useState(false);
  const [mathChallengeModalVisible, setMathChallengeModalVisible] =
    useState(false);
  const [secondConfirmModalVisible, setSecondConfirmModalVisible] =
    useState(false);
  const [mathAnswer, setMathAnswer] = useState("");
  const [mathProblem, setMathProblem] = useState<{
    num1: number;
    num2: number;
    answer: number;
  } | null>(null);
  const [pendingAdminUser, setPendingAdminUser] = useState<AuthUser | null>(
    null
  );

  const [editingAdminUserId, setEditingAdminUserId] = useState<string | null>(
    null
  );
  const [editingAdminName, setEditingAdminName] = useState("");
  const [savingAdminName, setSavingAdminName] = useState(false);

  // Add state for create user modal
  const [createUserModalVisible, setCreateUserModalVisible] = useState(false);
  const [createUserForm] = Form.useForm();

  // Add state for remove admin confirmation modal
  const [removeAdminModalVisible, setRemoveAdminModalVisible] = useState(false);

  // Note: Companies are now loaded by the edge function, no separate loading state needed

  // Get the auth token for API requests
  const getAuthToken = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token;
  };

  // Fetch users with their associated companies
  const fetchUsersWithCompanies = async (
    params?: Partial<PaginationParams>
  ) => {
    try {
      setLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Default values for pagination and sorting
      const page = params?.page || pagination.current;
      const pageSize = params?.pageSize || pagination.pageSize;
      const search = params?.searchQuery || searchQuery;
      const sort = params?.sortField || sortField;
      const order = params?.sortOrder || sortOrder;

      // Create query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("pageSize", pageSize.toString());

      if (search) {
        queryParams.append("search", search);
      }

      if (sort) {
        queryParams.append("sortField", sort);
      }

      if (order) {
        queryParams.append("sortOrder", order);
      }

      // Call the Supabase Edge Function for admin users with query parameters
      const { data: usersData, error: usersError } =
        await supabase.functions.invoke(
          `admin-users/list?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

      if (usersError) throw usersError;

      // Ensure we have admin users data for marking
      if (adminUsers.length === 0) {
        await fetchAdminUsers();
      }

      // Extract pagination data and users
      const responseData = usersData as UsersResponse;

      // Add is_admin property to users
      // Note: company_names are already populated by the edge function
      const usersWithAdminStatus = responseData.users.map((user: AuthUser) => ({
        ...user,
        is_admin: adminUsers.some((admin) => admin.user_id === user.id),
        company_names: user.company_names || [],
      }));

      // Set users directly - no need to fetch companies again
      setUsers(usersWithAdminStatus);

      // Debug user verification status
      console.log("User verification status:");
      usersWithAdminStatus.forEach((user) => {
        console.log(`User ${user.email}:`, {
          email_confirmed_at: user.email_confirmed_at,
          user_metadata_email_verified: user.user_metadata?.email_verified,
          verification_status:
            user.user_metadata?.email_verified === true &&
            user.email_confirmed_at !== null
              ? "Verified"
              : "Not Verified",
        });
      });

      // Update pagination state
      setPagination({
        current: responseData.page,
        pageSize: responseData.pageSize,
        total: responseData.total,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error(
        "Failed to fetch users. Make sure you have admin privileges."
      );
    } finally {
      setLoading(false);
    }
  };

  // Replace existing fetchUsers function with the new one
  const fetchUsers = fetchUsersWithCompanies;

  // Fetch admin users
  const fetchAdminUsers = async () => {
    try {
      setAdminLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function for admin users list
      const { data, error } = await supabase.functions.invoke(
        "admin-users/list-admins",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error) throw error;

      // Get emails from auth.users for each admin
      if (data?.admins && data.admins.length > 0) {
        const adminsWithEmails = await Promise.all(
          data.admins.map(async (admin: AdminUser) => {
            try {
              // Call function to get user details
              const { data: userData, error: userError } =
                await supabase.functions.invoke("admin-users/get-user", {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  body: {
                    userId: admin.user_id,
                  },
                });

              if (userError) throw userError;

              return {
                ...admin,
                email: userData?.user?.email || "Unknown",
              };
            } catch (err) {
              console.error("Error fetching user details:", err);
              return admin;
            }
          })
        );

        setAdminUsers(adminsWithEmails || []);
      } else {
        setAdminUsers(data?.admins || []);
      }
    } catch (error) {
      console.error("Error fetching admin users:", error);
      message.error("Failed to fetch admin users");
    } finally {
      setAdminLoading(false);
    }
  };

  const handleStartEditAdminName = (record: AdminUser) => {
    setEditingAdminUserId(record.user_id);
    setEditingAdminName(record.name ?? "");
  };

  const handleCancelEditAdminName = () => {
    setEditingAdminUserId(null);
    setEditingAdminName("");
  };

  const handleSaveAdminName = async (userId: string) => {
    const trimmed = editingAdminName.trim();
    try {
      setSavingAdminName(true);
      const { error } = await supabase
        .from("admin_users")
        .update({ name: trimmed || null })
        .eq("user_id", userId);

      if (error) throw error;
      message.success("Name updated");
      setAdminUsers((prev) =>
        prev.map((a) =>
          a.user_id === userId ? { ...a, name: trimmed || null } : a
        )
      );
      handleCancelEditAdminName();
    } catch (err) {
      console.error("Error updating admin name:", err);
      message.error("Failed to update name");
    } finally {
      setSavingAdminName(false);
    }
  };

  useEffect(() => {
    if (activeTab === "1") {
      fetchUsers();
    } else {
      fetchAdminUsers();
    }
    trackFeatureUsage("admin_users");
  }, [activeTab]);

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchText(value);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set a new timeout for debouncing
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      fetchUsers({
        page: 1, // Reset to first page for new search
        searchQuery: value,
      });
    }, 500); // 500ms debounce

    setSearchTimeout(timeout);
  };

  // Handle table change for pagination, sorting
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<AuthUser> | SorterResult<AuthUser>[]
  ) => {
    // Handle sorting
    let sortF: string | undefined;
    let sortO: "ascend" | "descend" | undefined;

    // Handle both single and multiple sorters
    if (!Array.isArray(sorter)) {
      if (sorter.field && sorter.order) {
        sortF = sorter.field as string;
        sortO = sorter.order;
      }
    }

    setSortField(sortF);
    setSortOrder(sortO);

    // Handle pagination - safely handle potentially undefined values
    fetchUsers({
      page: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
      sortField: sortF,
      sortOrder: sortO,
    });
  };

  // Edit user
  const handleEditUser = (user: AuthUser) => {
    setSelectedUser(user);
    form.setFieldsValue({
      email: user.email,
      first_name: user.user_metadata?.first_name || "",
      last_name: user.user_metadata?.last_name || "",
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedUser) return;

      setLoading(true);

      // Check if email has changed
      const emailChanged = values.email !== selectedUser.email;

      // Update email if changed (using the admin-users-update-email function)
      if (emailChanged) {
        const { error: emailError } = await supabase.functions.invoke(
          "admin-users-update-email",
          {
            method: "POST",
            body: {
              userId: selectedUser.id,
              email: values.email,
            },
          }
        );

        if (emailError) {
          throw new Error(`Failed to update email: ${emailError.message}`);
        }
      }

      // Update first name and last name using the admin-users edge function
      const { error: metadataError } = await supabase.functions.invoke(
        "admin-users/update",
        {
          method: "PATCH",
          body: {
            userId: selectedUser.id,
            first_name: values.first_name,
            last_name: values.last_name,
          },
        }
      );

      if (metadataError) {
        throw new Error(
          `Failed to update user metadata: ${metadataError.message}`
        );
      }

      message.success(
        emailChanged
          ? "User updated successfully (including email)"
          : "User updated successfully"
      );
      fetchUsers();
      setEditModalVisible(false);
    } catch (error) {
      console.error("Error updating user:", error);
      message.error(
        error instanceof Error ? error.message : "Failed to update user"
      );
    } finally {
      setLoading(false);
    }
  };

  // Download users function
  const handleDownloadUsers = async () => {
    try {
      message.loading("Fetching all users for export...", 0);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Fetch all users without pagination
      const queryParams = new URLSearchParams();
      queryParams.append("page", "1");
      queryParams.append("pageSize", "10000"); // Large number to get all users

      // Apply search filter if there's a search query
      if (searchQuery) {
        queryParams.append("search", searchQuery);
      }

      const { data: usersData, error: usersError } =
        await supabase.functions.invoke(
          `admin-users/list?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

      if (usersError) throw usersError;

      const responseData = usersData as UsersResponse;
      const allUsers = responseData.users;

      // Add is_admin property to users
      const usersWithAdminStatus = allUsers.map((user: AuthUser) => ({
        ...user,
        is_admin: adminUsers.some((admin) => admin.user_id === user.id),
      }));

      // Prepare CSV data with only first name, last name, and email
      const csvData = usersWithAdminStatus.map((user) => ({
        "First Name": user.user_metadata?.first_name || "",
        "Last Name": user.user_metadata?.last_name || "",
        Email: user.email || "",
      }));

      // Convert to CSV string
      const headers = ["First Name", "Last Name", "Email"];
      const csvContent = [
        headers.join(","),
        ...csvData.map((row) =>
          headers
            .map((header) => {
              const value = row[header as keyof typeof row] || "";
              // Escape quotes and wrap in quotes if contains comma or quote
              const escapedValue = value.toString().replace(/"/g, '""');
              return escapedValue.includes(",") || escapedValue.includes('"')
                ? `"${escapedValue}"`
                : escapedValue;
            })
            .join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `users_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy(); // Clear loading message
      message.success(`Exported ${csvData.length} users successfully`);
      trackFeatureUsage("admin_users_export");
    } catch (error) {
      message.destroy(); // Clear loading message
      console.error("Error exporting users:", error);
      message.error("Failed to export users");
    }
  };

  // Generate a random math problem
  const generateMathProblem = () => {
    const num1 = Math.floor(Math.random() * 20) + 1; // 1-20
    const num2 = Math.floor(Math.random() * 20) + 1; // 1-20
    const answer = num1 + num2;
    setMathProblem({ num1, num2, answer });
    setMathAnswer("");
    return { num1, num2, answer };
  };

  // Start the admin addition process with first confirmation
  const initiateAddAdmin = (user: AuthUser) => {
    setPendingAdminUser(user);
    setFirstConfirmModalVisible(true);
  };

  // Handle first confirmation - proceed to math challenge
  const handleFirstConfirm = () => {
    setFirstConfirmModalVisible(false);
    generateMathProblem();
    setMathChallengeModalVisible(true);
  };

  // Handle math challenge submission
  const handleMathChallengeSubmit = () => {
    if (!mathProblem) return;

    const userAnswer = parseInt(mathAnswer, 10);
    if (isNaN(userAnswer) || userAnswer !== mathProblem.answer) {
      message.error("Incorrect answer. Please try again.");
      generateMathProblem(); // Generate new problem
      return;
    }

    // Math challenge passed, show second confirmation
    setMathChallengeModalVisible(false);
    setSecondConfirmModalVisible(true);
  };

  // Handle second confirmation - actually add admin
  const handleSecondConfirm = async () => {
    if (!pendingAdminUser) return;

    try {
      setConfirmLoading(true);
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to add admin role
      const { error } = await supabase.functions.invoke(
        "admin-users/add-admin",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: pendingAdminUser.id,
          },
        }
      );

      if (error) throw error;

      message.success(`Admin privileges granted to ${pendingAdminUser.email}`);
      setSecondConfirmModalVisible(false);
      setPendingAdminUser(null);

      // Refresh admin users list
      await fetchAdminUsers();

      // Refresh users list with current search query and pagination preserved
      await fetchUsers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        searchQuery: searchQuery,
        sortField: sortField,
        sortOrder: sortOrder,
      });
    } catch (error) {
      console.error("Error adding admin role:", error);
      message.error(
        error instanceof Error ? error.message : "Failed to add admin role"
      );
      setSecondConfirmModalVisible(false);
      setPendingAdminUser(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Cancel admin addition process
  const cancelAddAdmin = () => {
    setFirstConfirmModalVisible(false);
    setMathChallengeModalVisible(false);
    setSecondConfirmModalVisible(false);
    setPendingAdminUser(null);
    setMathAnswer("");
    setMathProblem(null);
  };

  // Handle create user
  const handleCreateUser = async () => {
    try {
      const values = await createUserForm.validateFields();
      setConfirmLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to create user
      const { data, error } = await supabase.functions.invoke(
        "admin-users/create-user",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            email: values.email,
            password: values.password,
            first_name: values.first_name || "",
            last_name: values.last_name || "",
          },
        }
      );

      if (error) throw error;

      message.success(
        `User ${values.email} created successfully. They can now login.`
      );
      setCreateUserModalVisible(false);
      createUserForm.resetFields();

      // Refresh users list with current search query and pagination preserved
      await fetchUsers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        searchQuery: searchQuery,
        sortField: sortField,
        sortOrder: sortOrder,
      });
    } catch (error) {
      console.error("Error creating user:", error);
      message.error(
        error instanceof Error ? error.message : "Failed to create user"
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  // Initiate remove admin process - show confirmation modal
  const initiateRemoveAdmin = (user: AuthUser) => {
    setPendingAdminUser(user);
    setRemoveAdminModalVisible(true);
  };

  // Handle remove admin confirmation - actually remove admin
  const handleRemoveAdminConfirm = async () => {
    if (!pendingAdminUser) return;

    try {
      setConfirmLoading(true);
      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to remove admin role
      const { error } = await supabase.functions.invoke(
        "admin-users/remove-admin",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: pendingAdminUser.id,
          },
        }
      );

      if (error) throw error;

      message.success(
        `Admin privileges removed from ${pendingAdminUser.email}`
      );
      setRemoveAdminModalVisible(false);
      setPendingAdminUser(null);

      // Refresh admin users list
      await fetchAdminUsers();

      // Refresh users list with current search query and pagination preserved
      await fetchUsers({
        page: pagination.current,
        pageSize: pagination.pageSize,
        searchQuery: searchQuery,
        sortField: sortField,
        sortOrder: sortOrder,
      });
    } catch (error) {
      console.error("Error removing admin role:", error);
      message.error("Failed to remove admin role");
      setRemoveAdminModalVisible(false);
      setPendingAdminUser(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Cancel remove admin
  const cancelRemoveAdmin = () => {
    setRemoveAdminModalVisible(false);
    setPendingAdminUser(null);
  };

  // Password reset handler
  const handlePasswordReset = async (user: AuthUser) => {
    setSelectedUser(user);
    setNewPassword("");
    setPasswordStrength(0);
    setPasswordResetModalVisible(true);
  };

  // Submit password reset
  const handlePasswordResetSubmit = async () => {
    if (!selectedUser) return;

    try {
      setConfirmLoading(true);

      // Use the Edge Function via the admin service
      const { error } = await resetUserPassword(selectedUser.id, newPassword);

      if (error) throw error;

      message.success("Password has been reset successfully");
      setPasswordResetModalVisible(false);
    } catch (error: unknown) {
      console.error("Error resetting password:", error);
      message.error("Failed to reset password");
    } finally {
      setConfirmLoading(false);
    }
  };

  // Check password strength
  const checkPasswordStrength = (password: string) => {
    // Simple password strength check
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    setPasswordStrength(strength);
    setNewPassword(password);
  };

  // Verify email handler
  const handleVerifyEmail = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      // Use Supabase Admin API directly to verify email
      const { error } = await supabase.auth.admin.updateUserById(user.id, {
        email_confirm: true,
        user_metadata: {
          ...user.user_metadata,
          email_verified: true,
        },
      });

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Email ${user.email} has been verified successfully`,
      });
      setActionModalVisible(true);
      fetchUsers();
    } catch (error: unknown) {
      console.error("Error verifying email:", error);
      setActionResult({
        success: false,
        message: `Failed to verify email: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Send password reset link handler
  const handleSendResetLink = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to send reset link
      const { data, error } = await supabase.functions.invoke(
        "admin-users/generate-reset-link",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: user.id,
            redirectTo: `${window.location.origin}/auth/reset-password`,
          },
        }
      );

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Password reset link generated: ${
          data?.link || "Link generated"
        }`,
      });
      setActionModalVisible(true);
    } catch (error: unknown) {
      console.error("Error generating reset link:", error);
      setActionResult({
        success: false,
        message: `Failed to generate reset link: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Send verification link handler
  const handleSendVerificationLink = async (user: AuthUser) => {
    if (!user) return;

    try {
      setConfirmLoading(true);

      // Use Supabase Admin API directly to verify email
      const { error } = await supabase.auth.admin.updateUserById(user.id, {
        email_confirm: true,
        user_metadata: {
          ...user.user_metadata,
          email_verified: true,
        },
      });

      if (error) throw error;

      setActionResult({
        success: true,
        message: `Email ${user.email} has been verified successfully`,
      });
      setActionModalVisible(true);
      fetchUsers();
    } catch (error: unknown) {
      console.error("Error verifying email:", error);
      setActionResult({
        success: false,
        message: `Failed to verify email: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
      setActionModalVisible(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Email change handlers
  const handleChangeEmail = (user: AuthUser) => {
    setSelectedUser(user);
    setNewEmail(user.email);
    emailChangeForm.setFieldsValue({ email: user.email });
    setEmailChangeModalVisible(true);
  };

  const handleEmailChangeSubmit = async () => {
    try {
      const values = await emailChangeForm.validateFields();

      if (!selectedUser) return;

      setConfirmLoading(true);

      const token = await getAuthToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Supabase Edge Function to update email
      const { error } = await supabase.functions.invoke(
        "admin-users-update-email",
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            userId: selectedUser.id,
            email: values.email,
          },
        }
      );

      if (error) throw error;

      message.success("Email updated and verified successfully");
      setEmailChangeModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating email:", error);
      message.error("Failed to update email");
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleEmailChangeCancel = () => {
    setEmailChangeModalVisible(false);
    setNewEmail("");
    emailChangeForm.resetFields();
  };

  // Define columns with proper typing
  const columns: ColumnsType<AuthUser> = [
    {
      title: "First Name",
      key: "first_name",
      render: (_, record) => record.user_metadata?.first_name || "-",
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.user_metadata?.first_name || "").localeCompare(
          b.user_metadata?.first_name || ""
        ),
    },
    {
      title: "Last Name",
      key: "last_name",
      render: (_, record) => record.user_metadata?.last_name || "-",
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.user_metadata?.last_name || "").localeCompare(
          b.user_metadata?.last_name || ""
        ),
    },
    {
      title: "Name",
      dataIndex: "user_metadata",
      key: "name",
      render: (metadata) => {
        const firstName = metadata?.first_name || "";
        const lastName = metadata?.last_name || "";
        return `${firstName} ${lastName}`.trim() || "-";
      },
      sorter: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email, record) => (
        <Space>
          {email}
          {record.is_admin && <Tag color="blue">Admin</Tag>}
        </Space>
      ),
      sorter: (a: AuthUser, b: AuthUser) =>
        (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Companies",
      dataIndex: "company_names",
      key: "companies",
      render: (companies: string[]) => {
        if (!companies || companies.length === 0) return "-";

        // If there are too many companies, show the first few with a count
        if (companies.length > 3) {
          return (
            <div>
              {companies.slice(0, 3).map((company, index) => (
                <Tag key={index} color="blue" style={{ marginBottom: "4px" }}>
                  {company}
                </Tag>
              ))}
              <Tag color="default">+{companies.length - 3} more</Tag>
            </div>
          );
        }

        // Otherwise show all companies
        return (
          <div>
            {companies.map((company, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: "4px" }}>
                {company}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Role",
      key: "role",
      render: (_, record) => record.user_metadata?.role || "user",
      filters: [
        { text: "Admin", value: "admin" },
        { text: "User", value: "user" },
      ],
      onFilter: (value, record) =>
        (record.user_metadata?.role || "user") === value,
    },
    {
      title: "Provider",
      key: "provider",
      render: (_, record) => record.app_metadata?.provider || "email",
      filters: [
        { text: "Email", value: "email" },
        { text: "Google", value: "google" },
      ],
      onFilter: (value, record) =>
        (record.app_metadata?.provider || "email") === value,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: AuthUser, b: AuthUser) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Last Sign In",
      dataIndex: "last_sign_in_at",
      key: "last_sign_in_at",
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString() : "Never",
      sorter: (a: AuthUser, b: AuthUser) => {
        const dateA = a.last_sign_in_at
          ? new Date(a.last_sign_in_at).getTime()
          : 0;
        const dateB = b.last_sign_in_at
          ? new Date(b.last_sign_in_at).getTime()
          : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Email Verified",
      key: "email_verified",
      width: 130,
      render: (_, record) => {
        // Check for verified email using both sources - require both to be true for verified status
        const isVerified =
          record.user_metadata?.email_verified === true &&
          record.email_confirmed_at !== null;

        return (
          <Tag color={isVerified ? "success" : "warning"}>
            {isVerified ? "Verified" : "Not Verified"}
          </Tag>
        );
      },
      filters: [
        { text: "Verified", value: true },
        { text: "Not Verified", value: false },
      ],
      onFilter: (value, record) => {
        const isVerified =
          record.user_metadata?.email_verified === true &&
          record.email_confirmed_at !== null;
        return isVerified === value;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: AuthUser) => (
        <Space wrap>
          <ActionButton
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
            title="Edit user details"
          />
          <Dropdown
            menu={{
              items: [
                {
                  key: "change-email",
                  label: "Change Email",
                  onClick: () => handleChangeEmail(record),
                },
                {
                  key: "reset-password",
                  label: "Reset Password",
                  onClick: () => handlePasswordReset(record),
                },
                {
                  key: "verify-email",
                  label: "Verify Email",
                  onClick: () => handleVerifyEmail(record),
                  disabled: record.email_confirmed_at !== null,
                },
                {
                  key: "send-reset-link",
                  label: "Send Reset Link",
                  onClick: () => handleSendResetLink(record),
                },
                {
                  key: "send-verification",
                  label: "Force Verify Email",
                  onClick: () => handleSendVerificationLink(record),
                  disabled: record.email_confirmed_at !== null,
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />}>More Actions</Button>
          </Dropdown>
          {record.is_admin ? (
            <ActionButton
              icon={<UnlockOutlined />}
              onClick={() => initiateRemoveAdmin(record)}
              title="Remove admin privileges"
              danger
            >
              Remove Admin
            </ActionButton>
          ) : (
            <ActionButton
              icon={<UserOutlined />}
              onClick={() => initiateAddAdmin(record)}
              title="Grant admin privileges"
              type="primary"
            >
              Make Admin
            </ActionButton>
          )}
        </Space>
      ),
    },
  ];

  // Admin users columns
  const adminColumns: ColumnsType<AdminUser> = [
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      render: (_, record: AdminUser) => {
        const isEditing = editingAdminUserId === record.user_id;
        if (isEditing) {
          return (
            <Space.Compact style={{ width: "100%" }}>
              <Input
                size="small"
                value={editingAdminName}
                onChange={(e) => setEditingAdminName(e.target.value)}
                onPressEnter={() => handleSaveAdminName(record.user_id)}
                disabled={savingAdminName}
                autoFocus
              />
              <Button
                size="small"
                type="primary"
                onClick={() => handleSaveAdminName(record.user_id)}
                loading={savingAdminName}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={handleCancelEditAdminName}
                disabled={savingAdminName}
              >
                Cancel
              </Button>
            </Space.Compact>
          );
        }
        return (
          <Space size="small">
            <span>{record.name ?? "—"}</span>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleStartEditAdminName(record)}
              title="Edit name"
            />
          </Space>
        );
      },
    },
    {
      title: "Email",
      key: "email",
      dataIndex: "email",
      render: (email) => email || "Unknown",
    },
    {
      title: "Added At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: AdminUser, b: AdminUser) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: AdminUser) => {
        const user = users.find((u) => u.id === record.user_id) || {
          id: record.user_id,
        };
        return (
          <ActionButton
            icon={<UnlockOutlined />}
            danger
            onClick={() => initiateRemoveAdmin(user as AuthUser)}
            title="Remove admin privileges"
          >
            Remove Admin
          </ActionButton>
        );
      },
    },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>User Management</Title>
      <Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
        Manage user accounts, roles and permissions
      </Text>

      <CompactTable>
        <Card style={{ marginBottom: 24 }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="All Users" key="1">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 16,
                }}
              >
                <Input
                  placeholder="Search users by email, name, or ID"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: 300 }}
                  allowClear
                />
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateUserModalVisible(true)}
                    title="Create a new user"
                  >
                    Create User
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadUsers}
                    title="Download all users (First Name, Last Name, Email)"
                  >
                    Download Users
                  </Button>
                </Space>
              </div>

              <Table
                dataSource={users}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                size="small"
                className="compact-table"
              />
            </TabPane>
            <TabPane tab="Admin Users" key="2">
              <Table
                dataSource={adminUsers}
                columns={adminColumns}
                rowKey="user_id"
                loading={adminLoading}
                pagination={{ pageSize: 20 }}
                size="small"
                className="compact-table"
              />
            </TabPane>
            <TabPane tab="Agent portal" key="3">
              <AgentUsersManager />
            </TabPane>
          </Tabs>
        </Card>
      </CompactTable>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={loading}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleEditSubmit}
            loading={loading}
          >
            Save Changes
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="details">
          <TabPane tab="User Details" key="details">
            <Form form={form} layout="vertical">
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Email is required" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
                extra="Changing the email will automatically verify the new email address"
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="user@example.com"
                />
              </Form.Item>
              <Form.Item label="First Name" name="first_name">
                <Input placeholder="John" />
              </Form.Item>
              <Form.Item label="Last Name" name="last_name">
                <Input placeholder="Doe" />
              </Form.Item>
            </Form>
          </TabPane>
          <TabPane tab="Email Preferences" key="email-preferences">
            {selectedUser && (
              <AdminUserEmailPreferences userId={selectedUser.id} />
            )}
          </TabPane>
        </Tabs>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete User"
        open={deleteModalVisible}
        onOk={() => {}}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete {selectedUser?.email}?</p>
        <p>
          This action cannot be undone. All user data will be permanently
          removed.
        </p>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        title="Reset User Password"
        open={passwordResetModalVisible}
        onOk={handlePasswordResetSubmit}
        confirmLoading={confirmLoading}
        onCancel={() => setPasswordResetModalVisible(false)}
      >
        <p>Set a new password for user: {selectedUser?.email}</p>
        <Form layout="vertical">
          <Form.Item
            label="New Password"
            required
            help={
              newPassword.length > 0
                ? `Password strength: ${
                    ["Very Weak", "Weak", "Fair", "Good", "Strong"][
                      passwordStrength
                    ]
                  }`
                : "Enter a new password"
            }
          >
            <Input.Password
              value={newPassword}
              onChange={(e) => checkPasswordStrength(e.target.value)}
              placeholder="Enter new password"
            />
          </Form.Item>
          {newPassword.length > 0 && (
            <Progress
              percent={passwordStrength * 20}
              status={
                passwordStrength < 2
                  ? "exception"
                  : passwordStrength < 4
                  ? "normal"
                  : "success"
              }
              showInfo={false}
            />
          )}
        </Form>
      </Modal>

      {/* Email Change Modal */}
      <Modal
        title="Change User Email"
        open={emailChangeModalVisible}
        onOk={handleEmailChangeSubmit}
        onCancel={handleEmailChangeCancel}
        confirmLoading={confirmLoading}
        okText="Save Changes"
        cancelText="Discard Changes"
      >
        <Form form={emailChangeForm} layout="vertical">
          <Form.Item
            label="New Email Address"
            name="email"
            rules={[
              { required: true, message: "Please enter an email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter new email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
          </Form.Item>
          <Text type="secondary">
            This will update the user's email address and automatically mark it
            as verified.
          </Text>
        </Form>
      </Modal>

      {/* Action Result Modal */}
      <Modal
        title={actionResult?.success ? "Success" : "Error"}
        open={actionModalVisible}
        onOk={() => setActionModalVisible(false)}
        onCancel={() => setActionModalVisible(false)}
        footer={[
          <Button
            key="copy"
            type="primary"
            onClick={() => {
              if (actionResult?.message.includes("http")) {
                const link =
                  actionResult.message.match(/(https?:\/\/[^\s]+)/g)?.[0];
                if (link) {
                  navigator.clipboard.writeText(link);
                  message.success("Link copied to clipboard");
                }
              }
            }}
          >
            Copy Link
          </Button>,
          <Button key="ok" onClick={() => setActionModalVisible(false)}>
            OK
          </Button>,
        ]}
      >
        <p>{actionResult?.message}</p>
      </Modal>

      {/* First Confirmation Modal */}
      <Modal
        title="⚠️ Confirm Admin Privilege Grant"
        open={firstConfirmModalVisible}
        onOk={handleFirstConfirm}
        onCancel={cancelAddAdmin}
        okText="Continue"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        width={500}
      >
        <Alert
          message="Warning: Admin Access"
          description={
            <div>
              <p style={{ marginBottom: 12 }}>
                You are about to grant{" "}
                <strong>full administrator privileges</strong> to:
              </p>
              <p style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
                {pendingAdminUser?.email}
              </p>
              <p style={{ marginBottom: 0 }}>
                Admin users have complete access to the system, including:
              </p>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>User management and permissions</li>
                <li>Company data and settings</li>
                <li>All bookings and invoices</li>
                <li>System configuration</li>
              </ul>
              <p style={{ marginTop: 16, marginBottom: 0, fontWeight: 600 }}>
                Are you sure you want to proceed?
              </p>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Modal>

      {/* Math Challenge Modal */}
      <Modal
        title="🔢 Security Verification"
        open={mathChallengeModalVisible}
        onOk={handleMathChallengeSubmit}
        onCancel={cancelAddAdmin}
        okText="Verify"
        cancelText="Cancel"
        okButtonProps={{ type: "primary" }}
        width={450}
      >
        <Alert
          message="Please solve this simple math problem to continue"
          description={
            <div style={{ marginTop: 16 }}>
              <p
                style={{ fontSize: 18, marginBottom: 16, textAlign: "center" }}
              >
                <strong style={{ fontSize: 24 }}>
                  {mathProblem?.num1} + {mathProblem?.num2} = ?
                </strong>
              </p>
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Your Answer:
                </Text>
                <Input
                  type="number"
                  value={mathAnswer}
                  onChange={(e) => setMathAnswer(e.target.value)}
                  placeholder="Enter the sum"
                  autoFocus
                  onPressEnter={handleMathChallengeSubmit}
                  style={{ fontSize: 16, textAlign: "center", height: 40 }}
                />
              </div>
            </div>
          }
          type="info"
          showIcon
        />
      </Modal>

      {/* Second Confirmation Modal */}
      <Modal
        title="⚠️ Final Confirmation Required"
        open={secondConfirmModalVisible}
        onOk={handleSecondConfirm}
        onCancel={cancelAddAdmin}
        okText="Yes, Grant Admin Access"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: confirmLoading }}
        width={500}
      >
        <Alert
          message="Last Chance to Cancel"
          description={
            <div>
              <p style={{ marginBottom: 12 }}>
                You are about to grant administrator privileges to:
              </p>
              <p
                style={{
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {pendingAdminUser?.email}
              </p>
              <p style={{ marginBottom: 0, fontWeight: 600, color: "#ff4d4f" }}>
                This action cannot be easily undone. The user will have full
                system access.
              </p>
              <p style={{ marginTop: 16, marginBottom: 0 }}>
                Click <strong>"Yes, Grant Admin Access"</strong> to confirm, or{" "}
                <strong>"Cancel"</strong> to abort.
              </p>
            </div>
          }
          type="error"
          showIcon
        />
      </Modal>

      {/* Remove Admin Confirmation Modal */}
      <Modal
        title="⚠️ Confirm Remove Admin Privileges"
        open={removeAdminModalVisible}
        onOk={handleRemoveAdminConfirm}
        onCancel={cancelRemoveAdmin}
        okText="Yes, Remove Admin"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: confirmLoading }}
        width={500}
      >
        <Alert
          message="Remove Administrator Access"
          description={
            <div>
              <p style={{ marginBottom: 12 }}>
                You are about to remove administrator privileges from:
              </p>
              <p
                style={{
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {pendingAdminUser?.email}
              </p>
              <p style={{ marginBottom: 0 }}>This user will lose access to:</p>
              <ul style={{ marginTop: 8, marginBottom: 16 }}>
                <li>Admin dashboard and settings</li>
                <li>User management functions</li>
                <li>System configuration access</li>
                <li>All admin-only features</li>
              </ul>
              <p
                style={{
                  marginTop: 16,
                  marginBottom: 0,
                  fontWeight: 600,
                  color: "#ff4d4f",
                }}
              >
                Are you sure you want to proceed?
              </p>
            </div>
          }
          type="warning"
          showIcon
        />
      </Modal>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={createUserModalVisible}
        onOk={() => createUserForm.submit()}
        onCancel={() => {
          setCreateUserModalVisible(false);
          createUserForm.resetFields();
        }}
        confirmLoading={confirmLoading}
        okText="Create User"
        cancelText="Cancel"
        width={500}
      >
        <Form
          form={createUserForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="user@example.com"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Password is required" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
            help="User will be able to login immediately with this password"
          >
            <Input.Password
              placeholder="Enter password"
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item label="First Name" name="first_name">
            <Input placeholder="John" />
          </Form.Item>
          <Form.Item label="Last Name" name="last_name">
            <Input placeholder="Doe" />
          </Form.Item>
          <Alert
            message="User Creation"
            description="The user will be created with email confirmed, so they can login immediately. No company will be linked initially."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Form>
      </Modal>
    </Container>
  );
};

export default UsersAdmin;
